import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';

const STORAGE_KEY = 'tutorialVideoProgress_serasa';

const HIDDEN_STYLE =
  'position:fixed;width:1px;height:1px;opacity:0;pointer-events:none;left:-9999px;top:-9999px;';
const VISIBLE_STYLE = 'width:100%;display:block;';

/**
 * Manages a single persistent <video> element so Picture-in-Picture
 * continues after the dialog is closed.
 */
@Injectable({ providedIn: 'root' })
export class TutorialVideoService implements OnDestroy {
  private videoEl: HTMLVideoElement | null = null;
  private loadedSrc = '';
  private exitedByCode = false;
  private saveInterval?: ReturnType<typeof setInterval>;
  private stepTimestamp = 0;

  /** Tracks whether the video is currently in PiP mode. */
  readonly isInPip$ = new BehaviorSubject<boolean>(false);

  /** Emits when the user closes PiP from the browser window (not programmatically). */
  readonly pipClosedByUser$ = new Subject<void>();

  get isInPip(): boolean {
    return this.isInPip$.value;
  }

  /** Returns (or lazily creates) the persistent video element. */
  getVideoElement(src: string): HTMLVideoElement {
    if (!this.videoEl) {
      this.videoEl = this.createVideoElement();
      document.body.appendChild(this.videoEl);
      this.attachEvents(this.videoEl);
    }

    if (this.loadedSrc !== src) {
      this.loadedSrc = src;
      this.videoEl.src = src;
      this.videoEl.addEventListener(
        'loadedmetadata',
        () => {
          if (!this.videoEl) return;
          const target = this.stepTimestamp > 0 ? this.stepTimestamp : (this.loadProgress() ?? 0);
          if (target > 0 && target < this.videoEl.duration - 1) {
            this.videoEl.currentTime = target;
          }
        },
        { once: true }
      );
    }

    return this.videoEl;
  }

  /** Moves the video element into a visible host container. */
  attachToHost(host: HTMLElement): void {
    if (!this.videoEl) return;
    this.videoEl.style.cssText = VISIBLE_STYLE;
    host.appendChild(this.videoEl);
  }

  /** Moves the video back to body as an invisible element (keeps PiP alive). */
  detachFromHost(): void {
    if (!this.videoEl) return;
    this.videoEl.style.cssText = HIDDEN_STYLE;
    document.body.appendChild(this.videoEl);
  }

  async enterPip(): Promise<void> {
    if (this.videoEl && document.pictureInPictureEnabled) {
      await this.videoEl.requestPictureInPicture().catch(() => {});
    }
  }

  async exitPip(): Promise<void> {
    if (!document.pictureInPictureElement) return;
    this.exitedByCode = true;
    await document.exitPictureInPicture().catch(() => {
      this.exitedByCode = false;
    });
  }

  saveProgress(time: number): void {
    try {
      localStorage.setItem(STORAGE_KEY, String(time));
    } catch { /* quota exceeded */ }
  }

  clearProgress(): void {
    localStorage.removeItem(STORAGE_KEY);
  }

  loadProgress(): number | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? parseFloat(raw) : null;
    } catch {
      return null;
    }
  }

  /** Updates the timestamp the video should jump to when the dialog next opens. */
  setStepTimestamp(seconds: number): void {
    this.stepTimestamp = seconds;
  }

  /** Seeks the video to the current step timestamp (called each time the dialog opens). */
  seekToStep(): void {
    if (!this.videoEl) return;
    this.videoEl.currentTime = this.stepTimestamp;
  }

  startAutoSave(): void {
    this.stopAutoSave();
    this.saveInterval = setInterval(() => {
      const v = this.videoEl;
      if (v && !v.paused && !v.ended) {
        this.saveProgress(v.currentTime);
      }
    }, 2000);
  }

  stopAutoSave(): void {
    if (this.saveInterval) {
      clearInterval(this.saveInterval);
      this.saveInterval = undefined;
    }
  }

  ngOnDestroy(): void {
    this.stopAutoSave();
    this.videoEl?.remove();
    this.videoEl = null;
  }

  private createVideoElement(): HTMLVideoElement {
    const el = document.createElement('video');
    el.style.cssText = HIDDEN_STYLE;
    el.playsInline = true;
    el.muted = true;
    el.controls = true;
    el.setAttribute('controlsList', 'nodownload noplaybackrate disablepictureinpicture');
    return el;
  }

  private attachEvents(video: HTMLVideoElement): void {
    video.addEventListener('enterpictureinpicture', () => {
      this.isInPip$.next(true);
    });

    video.addEventListener('leavepictureinpicture', () => {
      this.isInPip$.next(false);
      this.saveProgress(video.currentTime);

      if (this.exitedByCode) {
        this.exitedByCode = false;
      } else {
        this.pipClosedByUser$.next();
      }
    });

    video.addEventListener('ended', () => {
      this.isInPip$.next(false);
      this.clearProgress();
    });
  }
}
